/**
 * Self-serve signup API.
 *
 * Two kinds of caller:
 * - **The lookup is anonymous**, so it bypasses the shared api client the same way `invitations`
 *   does: the coach has no account yet, and a token must not be sent even when a Firebase user
 *   happens to be signed in.
 * - **Create, checkout and status use the shared client**, which attaches the current Firebase ID
 *   token. Create is the one call made with a Firebase account that has no RosterPilot user yet;
 *   the API gives that path a narrow identity rather than a 401.
 *
 * Every refusal is thrown as a `SignupError` whose `code` comes from the response body, never from
 * the message text, so pages branch on the server's own vocabulary.
 */

import { ApiError, get, post } from './api';
import { parsePaymentTrial } from './paymentTrial';
import { parsePlanTierOffers, plainDate, positiveCents, positivePercent } from './planTierOffers';
import { isSport, parseSportPrices } from './sportPrices';
import type {
  CreateSignupRequest,
  CreateSignupResponse,
  SignupCheckoutRequest,
  SignupCheckoutResponse,
  SignupErrorCode,
  SignupLookupRequest,
  SignupLookupResponse,
  SignupSchool,
  SignupSport,
  SignupStatusResponse,
} from '../types/signup';
import type { OrganizationPaymentTrial } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';
const LOOKUP_URL = `${API_BASE_URL}/api/public/signup/lookup`;

export const RATE_LIMITED_MESSAGE = 'Too many attempts, try again later.';
export const NETWORK_MESSAGE =
  'We could not reach RosterPilot. Check your connection and try again.';
export const UNKNOWN_MESSAGE = 'Something went wrong on our end. Please try again.';

const KNOWN_CODES: ReadonlySet<string> = new Set<SignupErrorCode>([
  'invalid_email',
  'invalid_name',
  'invalid_sport',
  'unverified_domain',
  'email_not_verified',
  'already_exists',
  'email_in_use',
  'already_registered',
  'unavailable',
  'not_awaiting_payment',
  'not_self_serve',
  'forbidden',
  'payment_provider_error',
  'payment_pending',
  'not_found',
  'terms_not_accepted',
  'legal_not_accepted',
  'invalid_plan_tier',
  'invalid_school_name',
  'school_name_in_use',
  'invalid_agreement_term',
]);

/** A refused signup call. `message` is always displayable. */
export class SignupError extends Error {
  readonly code: SignupErrorCode;
  readonly status: number;

  constructor(code: SignupErrorCode, message: string, status: number) {
    super(message);
    this.name = 'SignupError';
    this.code = code;
    this.status = status;
  }
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

/** Status + parsed body to a `SignupError`. The body's `code` wins; the status is the fallback. */
const toSignupError = (status: number, body: unknown): SignupError => {
  const record = (body ?? {}) as { code?: unknown; message?: unknown };
  const bodyCode = typeof record.code === 'string' ? record.code : null;
  const message = text(record.message);

  if (status === 429) {
    // The rate limiter answers with an empty body.
    return new SignupError('rate_limited', RATE_LIMITED_MESSAGE, status);
  }

  if (bodyCode && KNOWN_CODES.has(bodyCode)) {
    return new SignupError(bodyCode as SignupErrorCode, message ?? UNKNOWN_MESSAGE, status);
  }

  if (status === 401) {
    return new SignupError(
      'unauthorized',
      message ?? 'Your session has expired. Sign in again to continue.',
      status
    );
  }

  return new SignupError('unknown', message ?? UNKNOWN_MESSAGE, status);
};

/** Wraps a shared-client failure. A status of 0 is the client's own "never reached the server". */
const fromApiError = (error: unknown): SignupError => {
  if (error instanceof SignupError) return error;
  if (error instanceof ApiError) {
    if (error.statusCode === 0) {
      return new SignupError('network', NETWORK_MESSAGE, 0);
    }
    return toSignupError(error.statusCode, error.response);
  }
  return new SignupError('unknown', UNKNOWN_MESSAGE, 0);
};

const isLookupStatus = (value: unknown): value is SignupLookupResponse['status'] =>
  value === 'eligible' || value === 'unverified' || value === 'unavailable';

const parseSchool = (value: unknown): SignupSchool | null => {
  if (typeof value !== 'object' || value === null) return null;
  const school: SignupSchool = { ...(value as SignupSchool) };
  const tierName = text((value as Record<string, unknown>).tierName);
  // `tierName` is only carried when the server sent a usable one, so a school from an older server
  // (or an older stored blob) reads exactly as it did before the field existed.
  delete school.tierName;
  if (tierName) school.tierName = tierName;
  return school;
};

/**
 * The yearly price for one sport: its own entry in `sportPrices`, or the legacy single price when
 * an older API sent no per-sport prices (it charged the same for every sport then).
 */
export const sportPriceCents = (
  lookup: Pick<SignupLookupResponse, 'sportPrices' | 'annualPriceCents'>,
  sport: SignupSport
): number | null => {
  const own = lookup.sportPrices.find((price) => price.sport === sport);
  if (own) return own.annualPriceCents;
  return lookup.sportPrices.length === 0 ? lookup.annualPriceCents : null;
};

/**
 * A lookup answer read through guards rather than cast, or null when it is not one this build
 * understands. Web and the API deploy separately, and the same shape is read back from localStorage
 * (`signupStorage`), so a status this build does not know must not render as an eligible school with
 * no price, and a sport it does not know is dropped rather than offered.
 */
export const parseLookupResponse = (value: unknown): SignupLookupResponse | null => {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (!isLookupStatus(record.status)) return null;

  const sports = Array.isArray(record.sports) ? record.sports.filter(isSport) : [];
  const threeYearDiscountPercent = positivePercent(record.threeYearDiscountPercent);
  const threeYearOfferEndsOn = threeYearDiscountPercent !== null ? plainDate(record.threeYearOfferEndsOn) : null;

  return {
    status: record.status,
    school: parseSchool(record.school),
    // Only an explicit false on an eligible answer asks for a school name. Absent (an older API, an
    // older stored blob) or anything else reads as matched, which is how every answer read before.
    schoolMatched: !(record.status === 'eligible' && record.schoolMatched === false),
    sports,
    sportPrices: parseSportPrices(record.sportPrices, sports),
    annualPriceCents:
      typeof record.annualPriceCents === 'number' ? record.annualPriceCents : null,
    // Only plans for a sport the school is offered; absent (an older API) reads as none.
    planTiers: parsePlanTierOffers(record.planTiers).filter((plan) => sports.includes(plan.sport)),
    // Only carried when offered, so a lookup from an older API (or an older stored blob) reads
    // exactly as it did before the field existed.
    ...(threeYearDiscountPercent !== null ? { threeYearDiscountPercent } : {}),
    ...(threeYearOfferEndsOn !== null ? { threeYearOfferEndsOn } : {}),
    currency: text(record.currency) ?? 'usd',
    // Only a positive whole number is a trial; anything else (an older API, a stored blob from
    // before the field) reads as none, which only changes copy.
    trialDays:
      typeof record.trialDays === 'number' && Number.isInteger(record.trialDays) && record.trialDays > 0
        ? record.trialDays
        : null,
    message: text(record.message),
  };
};

/**
 * What a school email address would buy: the school and the yearly price.
 *
 * Anonymous. The body carries the email and nothing else. It does not say whether the school's
 * program already exists (that would disclose the customer list): `sports` lists every sport the
 * school offers, and a taken program is refused by `createSignup` with `already_exists`.
 */
export const lookupSignup = async (email: string): Promise<SignupLookupResponse> => {
  const body: SignupLookupRequest = { email };
  let response: Response;

  try {
    response = await fetch(LOOKUP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SignupError('network', NETWORK_MESSAGE, 0);
  }

  const parsed: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw toSignupError(response.status, parsed);
  }

  const lookup = parseLookupResponse(parsed);
  if (!lookup) {
    throw new SignupError('unknown', UNKNOWN_MESSAGE, response.status);
  }

  return lookup;
};

/**
 * The two trial fields every signup state carries, read through guards. An older API sends
 * neither, and without a trial "awaiting payment" meant blocked, so that is what a missing
 * `accessBlocked` reads as.
 */
type TrialState = {
  awaitingPayment: boolean;
  accessBlocked: boolean;
  trial: OrganizationPaymentTrial | null;
};

type PlanState = { planTierKey: string | null; planTierName: string | null };

const withTrialState = <
  T extends Partial<Record<keyof TrialState | keyof PlanState, unknown>>,
>(
  response: T
): T & TrialState & PlanState => {
  const awaitingPayment = response?.awaitingPayment === true;
  return {
    ...response,
    // Absent from an API older than plans: no plan to name.
    planTierKey: text(response?.planTierKey),
    planTierName: text(response?.planTierName),
    awaitingPayment,
    accessBlocked:
      typeof response?.accessBlocked === 'boolean' ? response.accessBlocked : awaitingPayment,
    trial: parsePaymentTrial(response?.trial),
  };
};

/**
 * Creates the program for the signed-in Firebase account. A new program starts its free trial, so
 * it is not blocked (`accessBlocked` false) and the coach goes straight into the app.
 *
 * Built field by field so the payload can never grow an email, school, school tier or price. The
 * one choice it carries besides the sport is the plan KEY the coach picked (baseball), and only when
 * there is one: the server prices it. The terms and privacy versions agreed to are the server's; the
 * client only says the coach agreed to both.
 */
export const createSignup = async (
  request: CreateSignupRequest
): Promise<CreateSignupResponse> => {
  let created: CreateSignupResponse;
  try {
    created = await post<CreateSignupResponse>('/api/public/signup', {
      firstName: request.firstName,
      lastName: request.lastName,
      sport: request.sport,
      acceptedTerms: request.acceptedTerms,
      acceptedPrivacy: request.acceptedPrivacy,
      ...(request.planTier ? { planTier: request.planTier } : {}),
      ...(request.schoolName ? { schoolName: request.schoolName } : {}),
      platform: 'web',
    });
  } catch (error) {
    throw fromApiError(error);
  }
  return withTrialState(created);
};

/**
 * Starts Stripe Checkout for the signed-in admin's own unpaid program, during or after the trial.
 * The body carries the agreement term only when the coach chose one; with no term no body is sent,
 * which keeps the stored term (and is all an older API understands).
 */
export const startSignupCheckout = async (
  request: SignupCheckoutRequest = {}
): Promise<SignupCheckoutResponse> => {
  let result: SignupCheckoutResponse;

  try {
    result = request.agreementTermYears
      ? await post<SignupCheckoutResponse>('/api/signup/checkout', {
          agreementTermYears: request.agreementTermYears,
        })
      : await post<SignupCheckoutResponse>('/api/signup/checkout');
  } catch (error) {
    throw fromApiError(error);
  }

  if (!text(result?.checkoutUrl)) {
    throw new SignupError('payment_provider_error', UNKNOWN_MESSAGE, 200);
  }

  return result;
};

/** The signed-in admin's own signup state. */
export const getSignupStatus = async (): Promise<SignupStatusResponse> => {
  let status: SignupStatusResponse;
  try {
    status = await get<SignupStatusResponse>('/api/signup/status');
  } catch (error) {
    throw fromApiError(error);
  }
  // Only an explicit true holds the page on "processing": an older API that does not send the
  // field must not strand a coach who has not paid.
  return {
    ...withTrialState(status),
    paymentPending: status?.paymentPending === true,
    // An older API sends none of the term fields: a 1-year agreement, no 3-year offer.
    agreementTermYears: status?.agreementTermYears === 3 ? 3 : 1,
    oneYearAnnualPriceCents: positiveCents(status?.oneYearAnnualPriceCents),
    threeYearAnnualPriceCents: positiveCents(status?.threeYearAnnualPriceCents),
    threeYearDiscountPercent: positivePercent(status?.threeYearDiscountPercent),
    threeYearOfferEndsOn: plainDate(status?.threeYearOfferEndsOn),
  };
};
